import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface ConfirmReplyState {
  confirmReplyInfo: {
    /** シミュレーション年月度  */
    simulationYm: string
    /** 支店コード */
    branchCd: string
    /** 支店名 */
    branchNm: string
    /** 業者名コード */
    supplierCd: string
    /** 業者名 */
    supplierNm: string
    /** 業者支店コード */
    supplierBranchCd: string
    /** 業者支店名 */
    supplierBranchNm: string
    /** 工場名コード */
    factoryCd: string
    /** 工場名 */
    factoryNm: string
    /** 費目コード */
    expenseCd: string
    /** 費目 */
    expense: string
    /** ヒアリングシートフラグ */
    hearingSheetFlg: number
    /** 回答年月 */
    replyYm: string
  }
  /** 登録状況 */
  regStatus: {
    /** 業者決定物件 */
    addStsCdSupplierDecisionBuilding: string
    /** 見積依頼物件 */
    addStsCdSupplierEstimationBuilding: string
    /** 他社物件 */
    addStsCdOtherCompanyBuilding: string
    /** 業者最大能力 */
    addStsCdSupplierMaxCapacity: string
    /** ヒアリングシートコード */
    addStsCdHearingSheet: string
  }
  /** 回答ステータスコード */
  replyStsCd: string
  /** スクロール位置（横スクロール） */
  location: {
    /** 回答確認 */
    confirmReplyLocation: number
    /** 業者決定物件 */
    supplierDecisionBuildingLocation: number
    /** 見積依頼物件 */
    supplierEstimationBuildingLocation: number
    /** 他社物件 */
    otherCompanyBuildingLocation: number
    /** 業者最大能力 */
    supplierMaxCapacityLocation: number
  }
  /** スクロール位置（縦スクロール） */
  locationTop: {
    /** 業者決定物件 */
    supplierDecisionBuildingLocation: number
    /** 業者追加物件 */
    additionalBuildingLocation: number
    /** 見積依頼物件 */
    supplierEstimationBuildingLocation: number
    /** 他社物件 */
    otherCompanyBuildingLocation: number
    /** 業者最大能力 */
    supplierMaxCapacityLocation: number
    /** 入場可能数 */
    ourCompanyCapacityLocation: number
  }
  /** 業者ヒアリングヘッダ情報 */
  headerInfo: {
    /** 回答コメント */
    replyComment: string | null
    /** 回答者(姓) */
    replyNmLast: string | null
    /** 回答者(名) */
    replyNmFirst: string | null
    /** 修正内容 */
    fixContent: string | null
    /** 添付資料パス */
    attachmentFilePath: string | null
  }
}

const initialState: ConfirmReplyState = {
  confirmReplyInfo: {
    simulationYm: '',
    branchCd: '',
    branchNm: '',
    supplierCd: '',
    supplierNm: '',
    supplierBranchCd: '',
    supplierBranchNm: '',
    factoryCd: '',
    factoryNm: '',
    expenseCd: '',
    expense: '',
    hearingSheetFlg: 1,
    replyYm: ''
  },
  regStatus: {
    addStsCdSupplierDecisionBuilding: '',
    addStsCdSupplierEstimationBuilding: '',
    addStsCdOtherCompanyBuilding: '',
    addStsCdSupplierMaxCapacity: '',
    addStsCdHearingSheet: ''
  },
  replyStsCd: '',
  location: {
    confirmReplyLocation: 0,
    supplierDecisionBuildingLocation: 0,
    supplierEstimationBuildingLocation: 0,
    otherCompanyBuildingLocation: 0,
    supplierMaxCapacityLocation: 0
  },
  locationTop: {
    supplierDecisionBuildingLocation: 0,
    additionalBuildingLocation: 0,
    supplierEstimationBuildingLocation: 0,
    otherCompanyBuildingLocation: 0,
    supplierMaxCapacityLocation: 0,
    ourCompanyCapacityLocation: 0
  },
  headerInfo: {
    replyComment: null,
    replyNmLast: null,
    replyNmFirst: null,
    fixContent: null,
    attachmentFilePath: null
  }
}

const confirmReplySlice = createSlice({
  name: 'confrimreply',
  initialState,
  reducers: {
    setConfrimReplyInfo: (
      state,
      action: PayloadAction<ConfirmReplyState['confirmReplyInfo']>
    ) => {
      state.confirmReplyInfo = action.payload
    },
    setRegStatus: (
      state,
      action: PayloadAction<ConfirmReplyState['regStatus']>
    ) => {
      state.regStatus = action.payload
    },
    setReplyStsCd: (
      state,
      action: PayloadAction<ConfirmReplyState['replyStsCd']>
    ) => {
      state.replyStsCd = action.payload
    },
    setConfirmReplyLocation: (
      state,
      action: PayloadAction<number>
    ) => {
      state.location.confirmReplyLocation = action.payload
    },
    setSupplierDecisionBuildingLocation: (
      state,
      action: PayloadAction<number>
    ) => {
      state.location.supplierDecisionBuildingLocation = action.payload
    },
    setSupplierEstimationBuildingLocation: (
      state,
      action: PayloadAction<number>
    ) => {
      state.location.supplierEstimationBuildingLocation = action.payload
    },
    setOtherCompanyBuildingLocation: (
      state,
      action: PayloadAction<number>
    ) => {
      state.location.otherCompanyBuildingLocation = action.payload
    },
    setSupplierMaxCapacityLocation: (
      state,
      action: PayloadAction<number>
    ) => {
      state.location.supplierMaxCapacityLocation = action.payload
    },
    setSupplierDecisionBuildingLocationTop: (
      state,
      action: PayloadAction<number>
    ) => {
      state.locationTop.supplierDecisionBuildingLocation = action.payload
    },
    setAdditionalBuildingLocationTop: (
      state,
      action: PayloadAction<number>
    ) => {
      state.locationTop.additionalBuildingLocation = action.payload
    },
    setSupplierEstimationBuildingLocationTop: (
      state,
      action: PayloadAction<number>
    ) => {
      state.locationTop.supplierEstimationBuildingLocation = action.payload
    },
    setOtherCompanyBuildingLocationTop: (
      state,
      action: PayloadAction<number>
    ) => {
      state.locationTop.otherCompanyBuildingLocation = action.payload
    },
    setSupplierMaxCapacityLocationTop: (
      state,
      action: PayloadAction<number>
    ) => {
      state.locationTop.supplierMaxCapacityLocation = action.payload
    },
    setOurCompanyCapacityLocationTop: (
      state,
      action: PayloadAction<number>
    ) => {
      state.locationTop.ourCompanyCapacityLocation = action.payload
    },
    setHeaderInfo: (
      state,
      action: PayloadAction<ConfirmReplyState['headerInfo']>
    ) => {
      state.headerInfo = action.payload
    }
  }
})

export const ConfirmReplyActions = confirmReplySlice.actions
export const ConfirmReplyReducers = confirmReplySlice.reducer
